// @ts-strict-ignore
import React, { useMemo } from 'react';
import type { MatrixModel } from 'flow/store/engine/types';
import {
  Button, ButtonVariant, IconName,
} from '@usealloy/component-library';
import type { Axis } from '../AxisHeaderDropdown';
import AxisHeaderExactInput from './components/AxisHeaderExactInput';
import AxisHeaderRangeInput from './components/AxisHeaderRangeInput';
import { StyledLastHeader } from './components/styles';
import { getValueOrUndefined, isValueUnboundedHighRange, isValueUnboundedLowRange } from './helpers';
import { useEditorAccess } from 'flow/editor/context';
import { getIsEditable } from 'flow/service/general';


export interface AxisHeaderInputProps {
  axis: Axis;
  axisSettings: MatrixModel['axis']['x' | 'y'];
  index: number;
  onChange: ({ axis, index, value }) => void;
  onDelete: (axis: Axis) => void;
}

export default function AxisHeaderInput({
  axis, axisSettings, index, onChange, onDelete,
}: AxisHeaderInputProps) {
  const { editorMode } = useEditorAccess();
  const inEditMode = getIsEditable(editorMode);
  const { axisHeader, axisHeaderDataType, order } = axisSettings;

  const isLastBucketInRange = axisHeaderDataType === 'number'
    && index === axisHeader.length - 2;

  // small edge case where if both ends are unbounded,
  // the last bucket is considered the 2nd bucket
  const isLastBucketWhereFullyUnbounded = isLastBucketInRange
    && axisHeader.length === 3
    && (isValueUnboundedLowRange(axisHeader[0])
      || isValueUnboundedHighRange(axisHeader[0]))
    && (isValueUnboundedLowRange(axisHeader.at(-1))
      || isValueUnboundedHighRange(axisHeader.at(-1)));

  const isLastBucketInExact = axisHeaderDataType === 'string'
    && index === axisHeader.length - 1;

  const allowDeleteOnLastBucket = (
    isLastBucketInExact || isLastBucketInRange
  ) && !isLastBucketWhereFullyUnbounded
    && inEditMode
    && index !== 0; // unless it's the first bucket being defined

  const AxisHeaderComponent: JSX.Element = useMemo(() => {
    switch (axisHeaderDataType) {
      case 'number': {
        return (
          <AxisHeaderRangeInput
            axis={axis}
            axisOrder={order}
            values={{
              left: {
                index,
                value: getValueOrUndefined(axisHeader[index]) as number,
              },
              right: {
                index: index + 1,
                value: getValueOrUndefined(axisHeader[index + 1]) as number,
              },
            }}
            onChange={onChange}
            isLastBucket={isLastBucketInRange}
          />
        );
      }
      case 'string':
        return (
          <AxisHeaderExactInput
            axis={axis}
            values={{
              exact: {
                index,
                value: getValueOrUndefined(axisHeader[index]) as string,
              },
            }}
            onChange={onChange}
          />
        );
      default:
        return null;
    }
  }, [axis, axisHeader, axisHeaderDataType, index, order, onChange]);

  return axis === 'X' && allowDeleteOnLastBucket ? (
    <StyledLastHeader
      data-dd-action-name={`matrix-axis-header-delete-${axis.toLowerCase()}`}
    >
      <Button
        style={{
          maxHeight: 24, maxWidth: 24, padding: 0, borderRadius: 'var(--cl-radius-1)',
        }}
        icon={IconName.Trash}
        onClick={() => onDelete(axis)}
        variant={ButtonVariant.tertiary}
      />
      {AxisHeaderComponent}
    </StyledLastHeader>
  ) : AxisHeaderComponent;
}
